import type { DevicePresence, RemoteDevice } from '../model.js';

/** Tones map straight onto the brand palette; see brand/tokens.css for their meaning. */
export type Tone = 'ok' | 'muted' | 'warn' | 'live' | 'danger';

export const presenceLabel: Readonly<Record<DevicePresence, string>> = {
  online: 'Online',
  offline: 'Offline',
  unchecked: 'Saved connection',
  revoked: 'Access revoked',
};

export const presenceTone: Readonly<Record<DevicePresence, Tone>> = {
  online: 'ok',
  offline: 'muted',
  unchecked: 'muted',
  revoked: 'danger',
};

export const platformLabel: Readonly<Record<RemoteDevice['platform'], string>> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  unknown: '',
};

const minute = 60_000;
const hour = 60 * minute;
const day = 24 * hour;

/** Short English relative time for list views. The full timestamp belongs in a title attribute. */
export function formatRelativeTime(iso: string | null, now = Date.now()): string {
  if (!iso) return 'not reported';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'not reported';
  const elapsed = Math.max(0, now - then);
  if (elapsed < 45_000) return 'just now';
  if (elapsed < hour) return `${Math.round(elapsed / minute)} min ago`;
  if (elapsed < day) return `${Math.round(elapsed / hour)} hr ago`;
  if (elapsed < 2 * day) return 'yesterday';
  if (elapsed < 14 * day) return `${Math.round(elapsed / day)} days ago`;
  const date = new Date(then);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Not reported';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Not reported' : date.toLocaleString();
}

/** Clock-style duration for session timers: 0:42, 12:05, 1:03:09. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Up to two initials for an avatar: "Alex Example" becomes "AE", "vulcan" becomes "V". */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters = parts
    .slice(0, 2)
    .map((part) => [...part][0] ?? '')
    .join('');
  return letters.toUpperCase() || '?';
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
